import { getAssetUrls } from '@/api/assets';
import { logger } from '@/utils/logger';
import { URL_CACHE_TTL } from '@/config/constants';

// ═══════════════════════════════════════════════════════════════════
// URL CACHE
// ═══════════════════════════════════════════════════════════════════

interface CacheEntry {
  url: string;
  expires: number;
}

const urlCache = new Map<string, CacheEntry>();

/**
 * Clear expired entries from cache
 */
function cleanExpiredCache(): void {
  const now = Date.now();
  for (const [id, entry] of urlCache.entries()) {
    if (entry.expires < now) {
      urlCache.delete(id);
    }
  }
}

/**
 * Clear the entire URL cache
 * Useful after logout or design switch
 */
export function clearAssetUrlCache(): void {
  urlCache.clear();
  logger.debug('AssetResolver', 'Cache cleared');
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; validEntries: number } {
  const now = Date.now();
  let validEntries = 0;
  for (const entry of urlCache.values()) {
    if (entry.expires > now) {
      validEntries++;
    }
  }
  return { size: urlCache.size, validEntries };
}

// ═══════════════════════════════════════════════════════════════════
// ASSET ID EXTRACTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Extract all asset IDs from a canvas JSON object
 * Ignores temporary IDs (starting with "temp_")
 */
export function extractAssetIds(json: unknown): string[] {
  const ids: string[] = [];

  function traverse(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;

    const record = obj as Record<string, unknown>;

    // Check for assetId property
    if (
      record.assetId &&
      typeof record.assetId === 'string' &&
      !record.assetId.startsWith('temp_')
    ) {
      ids.push(record.assetId);
    }

    // Traverse objects array (Fabric.js structure)
    if (Array.isArray(record.objects)) {
      record.objects.forEach(traverse);
    }
  }

  traverse(json);

  // Return unique IDs
  return [...new Set(ids)];
}

// ═══════════════════════════════════════════════════════════════════
// URL INJECTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Inject URLs into canvas JSON objects based on asset IDs
 */
function injectUrls(
  json: unknown,
  urls: Record<string, string | null>
): unknown {
  // Deep clone to avoid mutating original
  const result = JSON.parse(JSON.stringify(json));

  function traverse(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;

    const record = obj as Record<string, unknown>;

    // If object has assetId and we have a URL for it, set src
    if (
      record.assetId &&
      typeof record.assetId === 'string' &&
      urls[record.assetId]
    ) {
      record.src = urls[record.assetId];
    }

    // Traverse objects array
    if (Array.isArray(record.objects)) {
      record.objects.forEach(traverse);
    }
  }

  traverse(result);
  return result;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN RESOLVER FUNCTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Resolve all asset IDs in a canvas JSON to current URLs
 *
 * Uses in-memory cache to avoid redundant API calls.
 * Call this before loading canvas data to ensure all images have valid URLs.
 *
 * @param canvasJson - The canvas JSON object (from canvasStateManager)
 * @returns Modified canvas JSON with resolved URLs
 *
 * @example
 * ```typescript
 * const positionState = canvasStateManager.masterPositions['front'];
 * const resolvedJson = await resolveCanvasAssets(positionState.json);
 * await deserializeUserObjects(resolvedJson, canvas);
 * ```
 */
export async function resolveCanvasAssets(
  canvasJson: unknown
): Promise<unknown> {
  // 1. Extract all asset IDs
  const assetIds = extractAssetIds(canvasJson);

  logger.debug(
    'AssetResolver',
    `Extracted ${assetIds.length} asset IDs`,
    assetIds
  );

  if (assetIds.length === 0) {
    return canvasJson;
  }

  // 2. Cleanup expired cache entries
  cleanExpiredCache();

  // 3. Check cache for already-resolved URLs
  const now = Date.now();
  const cached: Record<string, string> = {};
  const missing: string[] = [];

  for (const id of assetIds) {
    const entry = urlCache.get(id);
    if (entry && entry.expires > now) {
      cached[id] = entry.url;
    } else {
      missing.push(id);
    }
  }

  // 4. Fetch missing URLs from backend
  let fetchedUrls: Record<string, string | null> = {};

  if (missing.length > 0) {
    logger.debug(
      'AssetResolver',
      `Cache hit: ${Object.keys(cached).length}, fetching: ${missing.length}`
    );

    try {
      const response = await getAssetUrls(missing);
      fetchedUrls = response.urls;

      // 5. Update cache with new URLs
      for (const [id, url] of Object.entries(fetchedUrls)) {
        if (url) {
          urlCache.set(id, {
            url,
            expires: Date.now() + URL_CACHE_TTL,
          });
        }
      }
    } catch (error) {
      logger.error('AssetResolver', 'Failed to fetch URLs:', error);
      // Continue with cached URLs only
    }
  } else {
    logger.debug(
      'AssetResolver',
      `Full cache hit for ${assetIds.length} assets`
    );
  }

  // 6. Merge cached and fetched URLs
  const allUrls: Record<string, string | null> = { ...fetchedUrls };
  for (const [id, url] of Object.entries(cached)) {
    allUrls[id] = url;
  }

  // 7. Inject URLs into canvas JSON
  return injectUrls(canvasJson, allUrls);
}

/**
 * Check if a canvas JSON has any unresolved assets
 * (assets with temp_ IDs that haven't been uploaded yet)
 */
export function hasUnresolvedAssets(canvasJson: unknown): boolean {
  function traverse(obj: unknown): boolean {
    if (!obj || typeof obj !== 'object') return false;

    const record = obj as Record<string, unknown>;

    if (
      record.assetId &&
      typeof record.assetId === 'string' &&
      record.assetId.startsWith('temp_')
    ) {
      return true;
    }

    if (Array.isArray(record.objects)) {
      return record.objects.some(traverse);
    }

    return false;
  }

  return traverse(canvasJson);
}

/**
 * Filter out objects with temporary asset IDs from canvas JSON
 * Use this before saving to prevent saving incomplete uploads
 */
export function filterTempAssets(canvasJson: unknown): unknown {
  const result = JSON.parse(JSON.stringify(canvasJson));

  if (
    result &&
    typeof result === 'object' &&
    Array.isArray((result as Record<string, unknown>).objects)
  ) {
    const record = result as Record<string, unknown[]>;
    record.objects = (record.objects ?? []).filter((obj) => {
      const item = obj as Record<string, unknown>;
      if (
        item.assetId &&
        typeof item.assetId === 'string' &&
        item.assetId.startsWith('temp_')
      ) {
        logger.warn(
          'AssetResolver',
          `Filtering out object with temp asset: ${item.assetId}`
        );
        return false;
      }
      return true;
    });
  }

  return result;
}

/**
 * Clean up src fields from objects that have assetId
 *
 * OPTIMIZATION: Keep valid HTTP(S) URLs for faster position switching.
 * Only remove blob: URLs (which are temporary and will become invalid).
 *
 * This eliminates the need for API calls during position switches,
 * as the URLs are already cached in the canvas JSON.
 */
export function stripAssetSrcFields(canvasJson: unknown): unknown {
  const result = JSON.parse(JSON.stringify(canvasJson));

  function traverse(obj: unknown): void {
    if (!obj || typeof obj !== 'object') return;

    const record = obj as Record<string, unknown>;

    // If has assetId and src
    if (record.assetId && record.src && typeof record.src === 'string') {
      // Always remove blob: URLs (temporary, will become invalid)
      if (record.src.startsWith('blob:')) {
        delete record.src;
      }
      // Remove base64 data: URLs only if asset is uploaded to S3
      // (local assets with no S3 counterpart still need the data URL)
      else if (record.src.startsWith('data:') && record.assetType === 's3') {
        delete record.src;
      }
    }

    if (Array.isArray(record.objects)) {
      record.objects.forEach(traverse);
    }
  }

  traverse(result);
  return result;
}
